import React, { useState, useRef, useCallback } from 'react';

interface VoiceRecorderButtonProps {
  onTranscriptionComplete: (text: string) => void;
  disabled?: boolean;
}

const VoiceRecorderButton: React.FC<VoiceRecorderButtonProps> = ({
  onTranscriptionComplete,
  disabled = false
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      streamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        transcribeAudio(audioBlob);
      };

      audioChunksRef.current = [];
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('Microphone access denied');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
  }, [isRecording]);

  const transcribeAudio = useCallback(async (audioBlob: Blob) => {
    try {
      setIsTranscribing(true);
      setError(null);

      // Use FormData instead of base64 to avoid stack overflow with large files
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('format', 'audio/webm');

      const response = await fetch('/api/neural-templates/transcribe', {
        method: 'POST',
        credentials: 'include',
        body: formData, // Send as FormData instead of JSON
      });

      if (!response.ok) {
        throw new Error('Transcription failed');
      }

      const result = await response.json() as { transcription?: string };
      const transcription = result.transcription || '';

      if (transcription.trim()) {
        onTranscriptionComplete(transcription.trim());
      } else {
        setError('No speech detected');
      }

    } catch (err) {
      console.error('Transcription error:', err);
      setError('Transcription failed');
    } finally {
      setIsTranscribing(false);
    }
  }, [onTranscriptionComplete]);

  const handleToggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // Auto-clear error after 3 seconds
  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="relative">
      <button
        onClick={handleToggleRecording}
        disabled={disabled || isTranscribing}
        className={`relative flex items-center justify-center w-8 h-8 rounded transition-all duration-200 ${
          isRecording 
            ? 'bg-red-500 hover:bg-red-600 text-white' 
            : isTranscribing
            ? 'bg-blue-400 cursor-not-allowed text-white'
            : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={
          isTranscribing 
            ? 'Transcribing...' 
            : isRecording 
            ? 'Click to stop recording' 
            : 'Record voice note'
        }
      >
        {isTranscribing ? (
          <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full"></div>
        ) : isRecording ? (
          // Red dot with pulse animation when recording
          <div className="relative">
            <div className="w-2 h-2 bg-white rounded-full"></div>
            <div className="absolute top-0 left-0 w-2 h-2 bg-white rounded-full animate-ping opacity-75"></div>
          </div>
        ) : (
          // Microphone icon when ready
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
          </svg>
        )}
      </button>

      {/* Error tooltip */}
      {error && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-red-100 text-red-700 text-xs rounded border border-red-200 whitespace-nowrap z-50">
          {error}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-red-200"></div>
        </div>
      )}

      {/* Recording indicator */}
      {isRecording && (
        <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
      )}
    </div>
  );
};

export default VoiceRecorderButton; 